import { createContext } from 'react'
import { DomainDto } from '../../dtos/domain'
import { Domain } from '@pushly/models/lib/structs/domain'
import {
    CreateBlueConicDomainIntegrationRequest,
    GetBlueConicDomainIntegrationResponse,
    GetDomainIntegrationResponse,
    GetLyticsDomainIntegrationResponse,
    GetShopifyDomainIntegrationResponse,
    GetSlackDomainIntegrationResponse,
    GetWordpressDomainIntegrationResponse,
    UpdateBlueConicIntegrationRequest,
} from '@pushly/models/lib/structs/domains'
import { DomainIntegrationProviders } from '@pushly/models/lib/enums/domain-integration-providers'
import { AbstractStruct } from '@pushly/models/lib/structs/abstract-struct'

type ActionRequestType = 'add' | 'edit' | 'rem'

export type ActionRequest<
    Provider extends DomainIntegrationProviders,
    ActionType extends ActionRequestType,
    DataType extends AbstractStruct = any,
> = {
    provider: Provider
    type: ActionType
    data: DataType
    processing?: boolean
}

export type BlueConicActionRequest<
    ActionType extends ActionRequestType,
    DataType extends AbstractStruct = ActionType extends 'add'
        ? CreateBlueConicDomainIntegrationRequest
        : ActionType extends 'edit'
        ? UpdateBlueConicIntegrationRequest
        : GetBlueConicDomainIntegrationResponse,
> = ActionRequest<DomainIntegrationProviders.BLUECONIC_REC, ActionType, DataType>

export type LyticsActionRequest<
    ActionType extends ActionRequestType,
    DataType extends AbstractStruct = GetLyticsDomainIntegrationResponse,
> = ActionRequest<DomainIntegrationProviders.LYTICS, ActionType, DataType>

export type ShopifyActionRequest<
    ActionType extends ActionRequestType,
    DataType extends AbstractStruct = GetShopifyDomainIntegrationResponse,
> = ActionRequest<DomainIntegrationProviders.SHOPIFY, ActionType, DataType>

export type SlackActionRequest<
    ActionType extends ActionRequestType,
    DataType extends AbstractStruct = GetSlackDomainIntegrationResponse,
> = ActionRequest<DomainIntegrationProviders.SLACK, ActionType, DataType>

export type WordpressActionRequest<
    ActionType extends ActionRequestType,
    DataType extends AbstractStruct = GetWordpressDomainIntegrationResponse,
> = ActionRequest<DomainIntegrationProviders.WORDPRESS, ActionType, DataType>

export type BaseDomainIntegrationsManagerContextState = {
    loading: boolean
    domain?: DomainDto | Domain
    integrations?: GetDomainIntegrationResponse[]
    integrations_reload_requested?: true
    actionRequest?:
        | BlueConicActionRequest<'add'>
        | BlueConicActionRequest<'edit'>
        | BlueConicActionRequest<'rem'>
        // | LyticsActionRequest<'add', CreateLyticsDomainIntegrationRequest>
        | LyticsActionRequest<'edit'>
        | LyticsActionRequest<'rem'>
        // | ShopifyActionRequest<'add', CreateShopifyDomainIntegrationRequest>
        | ShopifyActionRequest<'edit'>
        | ShopifyActionRequest<'rem'>
        // | SlackActionRequest<'add', CreateSlackDomainIntegrationRequest>
        | SlackActionRequest<'edit'>
        | SlackActionRequest<'rem'>
        // | WordpressActionRequest<'add', CreateWordpressDomainIntegrationRequest>
        | WordpressActionRequest<'edit'>
        | WordpressActionRequest<'rem'>
}

export type PromptEditorContextLoadingState = BaseDomainIntegrationsManagerContextState & {
    loading: true
}

export type PromptEditorContextLoadedState = BaseDomainIntegrationsManagerContextState & {
    loading: false
    domain: DomainDto | Domain
    integrations: GetDomainIntegrationResponse[]
}

export type DomainIntegrationsManagerContextState = PromptEditorContextLoadingState | PromptEditorContextLoadedState

export const DomainIntegrationsManagerContext = createContext<DomainIntegrationsManagerContextState>(null!)
